import { inject, Injectable } from '@angular/core';
import { CustomerModel } from '@libs/account/models';
import { CapacitorService, EnvironmentService } from '@libs/shared/common';
import {
  appNavigationItems,
  appNavigationSpecialItems,
  appNavigationSpecialItemsIds,
  NavItemModel,
  NavModel,
  quickOrderNavItem,
} from '@libs/shared/models';
import { catchError, Observable, of } from 'rxjs';
import { CapabilityService } from '../../capability';
import { ConnectorService } from '../../connector';

@Injectable({ providedIn: 'root' })
export class NavigationRepositoryService {
  private readonly connector: ConnectorService = inject(ConnectorService);
  private readonly capacitorService: CapacitorService = inject(CapacitorService);
  private readonly env: EnvironmentService = inject(EnvironmentService);
  private readonly capabilityService: CapabilityService = inject(CapabilityService);
  private readonly specialItems: typeof appNavigationSpecialItems = appNavigationSpecialItems;
  private readonly specialIds: typeof appNavigationSpecialItemsIds = appNavigationSpecialItemsIds;

  public getMainNavigation(customer?: CustomerModel): Observable<NavModel> {
    if (!customer) {
      return of({});
    }

    return this.connector.connect<NavModel>('navigation', 'navigation', {
      searchParams: this.formatSearchParams(customer),
    }).pipe(
      catchError((error: Error) => {
        console.error(error);

        return of({});
      }),
    );
  }

  public getHomeNavigation(customer?: CustomerModel): Observable<NavModel> {
    let item: NavItemModel;
    let id: string;

    if (customer) {
      item = this.specialItems.home;
      id = customer.customerCategorizing?.bmi ? this.specialIds.home.bmi : this.specialIds.home.authenticated;
    } else {
      item = this.specialItems.login;
      id = this.capacitorService.isCapacitorApp() ? this.specialIds.home.anonymousApp : this.specialIds.home.anonymousWeb;
    }

    return of({
      children: [
        { ...item, id },
      ],
    });
  }

  public getFooterNavigation(customer?: CustomerModel): Observable<NavModel> {
    let faqId: string = this.specialIds.faq.anonymous;

    if (customer) {
      faqId = customer.customerCategorizing?.bmi ? this.specialIds.faq.bmi : this.specialIds.faq.authenticated;
    }

    return of({
      children: [
        ...appNavigationItems.footer,
        {
          ...appNavigationSpecialItems.faq,
          id: faqId,
        },
      ],
    });
  }

  public getUserNavigation(customer?: CustomerModel): Observable<NavModel> {
    const items: NavItemModel[] = [
      ...appNavigationItems.account,
    ];

    if (this.capabilityService.isAvailable('services.srm')()) {
      items.push(...appNavigationItems.service);
    }

    items.push(...appNavigationItems.auth);

    if (this.env.type() !== 'prod') {
      items.push(...appNavigationItems.dev);
    }

    return of({ children: customer ? items : undefined });
  }

  public getAgentNavigation(): Observable<NavModel> {
    return of({
      children: appNavigationItems.agent,
    });
  }

  public getCatalogNavigation(customer?: CustomerModel): Observable<NavModel> {
    return of({ children: customer ? appNavigationItems.catalog : undefined });
  }

  public getOrdersNavigation(isMobile: boolean, customer?: CustomerModel): Observable<NavModel> {
    const navigation: NavItemModel[] = [
      ...appNavigationItems.orders,
    ];

    if (!isMobile) {
      navigation.push(quickOrderNavItem);
    }

    return of({ children: customer ? navigation : undefined });
  }

  public getCheckoutNavigation(customer?: CustomerModel): Observable<NavModel> {
    return of({ children: customer ? appNavigationItems.checkout : undefined });
  }

  public getNavbarNavigationBase(customer?: CustomerModel): Observable<NavModel> {
    return of({ children: customer ? appNavigationItems.navbar : undefined });
  }

  private formatSearchParams(customer?: CustomerModel): Record<string, string> {
    const searchParams: Record<string, string> = {};

    if (customer?.customerCategorizing?.bmi) {
      searchParams['customer-scope'] = 'bmi';
    }

    return searchParams;
  }
}
